import React from "react";
import { Button, Card, Chip, IconButton, Skeleton, Stack, Typography } from "@mui/joy";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import { Link as RouterLink } from "react-router-dom";

import { useDonationsStore } from "../../store/useDonationsStore";
import { useErrorStore } from "../../store/useErrorStore";
import { downloadDonationReceipt } from "../../api";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const rowsPerPage = 5;

export default function DonationMobile() {
  const { donations, loading } = useDonationsStore();
  const { error } = useErrorStore();
  const [page, setPage] = React.useState(0);

  const totalPages = Math.max(1, Math.ceil(donations.length / rowsPerPage));

  React.useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  const paginatedDonations = React.useMemo(
    () => donations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [donations, page],
  );

  const handleDownloadReceipt = async (donationId: number) => {
    try {
      const blob = await downloadDonationReceipt(donationId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `donation-receipt-${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download receipt", err);
    }
  };

  if (loading && donations.length === 0) {
    return (
      <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" } }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} variant="outlined" sx={{ borderRadius: "xl", p: 2.5 }}>
            <Skeleton level="body-lg" sx={{ width: "60%" }} />
            <Skeleton level="body-sm" sx={{ width: "40%" }} />
            <Skeleton level="body-sm" sx={{ width: "75%" }} />
          </Card>
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: { xs: "flex", sm: "none" },
          borderRadius: "xl",
          p: 2.5,
          color: "danger.600",
        }}
      >
        <Typography level="body-sm">{error}</Typography>
      </Card>
    );
  }

  if (!loading && donations.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: { xs: "flex", sm: "none" },
          borderRadius: "xl",
          p: 2.5,
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <VolunteerActivismRoundedIcon color="primary" />
        <Typography level="title-sm">No offerings recorded yet</Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
          Once donations are recorded, you will see the devotee’s seva history here.
        </Typography>
      </Card>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" } }}>
      {paginatedDonations.map((donation) => (
        <Card
          key={donation.id}
          variant="outlined"
          sx={{
            borderRadius: "xl",
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Stack spacing={0.25}>
            <Typography level="title-sm" sx={{ fontWeight: 700 }}>
              {donation.name || "Anonymous devotee"}
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.secondary" }}>
              {donation.email || donation.phone || "–"}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography level="h4" sx={{ fontWeight: 700 }}>
              {currencyFormatter.format(Number(donation.amount ?? 0))}
            </Typography>
            <Chip size="sm" variant="soft" color="primary">
              {donation.paymentMethod || "–"}
            </Chip>
          </Stack>

          <Stack spacing={0.5}>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
              {donation.purpose || "Purpose not specified"}
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.secondary" }}>
              {donation.head || donation.subPurpose || donation.event || "Seva head not recorded"}
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              {donation.date ? new Date(donation.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Date unavailable"}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="soft"
              startDecorator={<VisibilityRoundedIcon fontSize="small" />}
              component={RouterLink}
              to={`/donations/details?id=${donation.id}`}
              sx={{ flex: 1 }}
            >
              View details
            </Button>
            <IconButton
              size="sm"
              variant="plain"
              color="primary"
              onClick={() => handleDownloadReceipt(donation.id)}
            >
              <DownloadRoundedIcon />
            </IconButton>
          </Stack>
        </Card>
      ))}

      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={page === 0}
          onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
        >
          <KeyboardArrowLeftRoundedIcon />
        </IconButton>
        <Typography level="body-sm">
          Page {Math.min(page + 1, totalPages)} of {totalPages}
        </Typography>
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={page >= totalPages - 1}
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
        >
          <KeyboardArrowRightRoundedIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}
